import React from 'react';
import { Link } from 'react-router-dom';
import { useWorkExperience } from '../../hooks';
import { Button, Card, CardHeader, CardBody, Spinner, Alert, VerificationBadge, GuardedEditButton } from '../../components';
import { getFieldOfWorkLabel } from '../../utils';
import type { WorkExperience } from '../../types';

const GAP_THRESHOLD_MONTHS = 3;

function monthsBetween(endDate: Date, startDate: Date): number {
  const years = startDate.getFullYear() - endDate.getFullYear();
  const months = startDate.getMonth() - endDate.getMonth();
  return years * 12 + months;
}

function formatGapDuration(months: number): string {
  if (months >= 12) {
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem > 0 ? `${years}y ${rem}m` : `${years} year${years !== 1 ? 's' : ''}`;
  }
  return `${months} month${months !== 1 ? 's' : ''}`;
}

interface GapItem {
  type: 'gap';
  months: number;
  from: string;
  to: string;
}

interface ExpItem {
  type: 'experience';
  data: WorkExperience;
}

/** `sorted` must be newest-first. Gap is between the older job's end and the newer job's start. */
function buildTimelineItems(sorted: WorkExperience[]): (ExpItem | GapItem)[] {
  const items: (ExpItem | GapItem)[] = [];

  for (let i = 0; i < sorted.length; i++) {
    items.push({ type: 'experience', data: sorted[i] });

    if (i < sorted.length - 1) {
      const newer = sorted[i];
      const older = sorted[i + 1];
      const olderEnd = older.is_current
        ? new Date()
        : older.end_date
          ? new Date(older.end_date)
          : null;
      const newerStart = new Date(newer.start_date);

      if (olderEnd) {
        const gap = monthsBetween(olderEnd, newerStart);
        if (gap > GAP_THRESHOLD_MONTHS) {
          items.push({
            type: 'gap',
            months: gap,
            from: olderEnd.toLocaleDateString(),
            to: newerStart.toLocaleDateString(),
          });
        }
      }
    }
  }

  return items;
}

export const Experience: React.FC = () => {
  const { workExperience, loading, error, deleteWorkExperience, clearError } = useWorkExperience();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Newest-first for display; gap analysis compares each job to the older one below it
  const newestFirst = [...workExperience].sort(
    (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );
  const timelineItems = buildTimelineItems(newestFirst);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Work Experience</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your employment history &amp; career timeline</p>
        </div>
        <Link to="/experience/add" className="self-start shrink-0">
          <Button>
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="danger" onClose={clearError}>{error}</Alert>
      )}

      {workExperience.length > 0 ? (
        <div className="space-y-3">
          {timelineItems.map((item, idx) => {
            if (item.type === 'gap') {
              return (
                <div key={`gap-${idx}`} className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <p className="text-sm text-amber-700">
                    <span className="font-semibold">Employment gap: {formatGapDuration(item.months)}</span>
                    <span className="text-amber-600 ml-1 text-xs">({item.from} – {item.to})</span>
                  </p>
                </div>
              );
            }

            const exp = item.data;
            return (
              <Card key={exp.id} collapsible>
                <CardHeader>
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-gray-900 break-words">{exp.job_title}</h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {exp.is_current && (
                          <span className="text-xs bg-brand-50 text-brand-600 border border-brand-200 px-2 py-0.5 rounded-full font-semibold">Current</span>
                        )}
                        <VerificationBadge status={exp.verification_status} />
                      </div>
                      <p className="text-sm font-medium text-gray-600 mt-1 break-words">{exp.employer}</p>
                      <div className="flex gap-1.5 mt-2.5">
                        <GuardedEditButton to={`/experience/${exp.id}/edit`} status={exp.verification_status} size="icon">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span className="sr-only">Edit</span>
                        </GuardedEditButton>
                        <Button variant="danger" size="icon" onClick={() => deleteWorkExperience(exp.id)} aria-label="Delete">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-gray-50 border border-gray-100 text-gray-500 px-2.5 py-1 rounded-full">
                      {new Date(exp.start_date).toLocaleDateString()} – {exp.is_current ? 'Present' : exp.end_date ? new Date(exp.end_date).toLocaleDateString() : ''}
                    </span>
                    {exp.location && (
                      <span className="text-xs bg-gray-50 border border-gray-100 text-gray-500 px-2.5 py-1 rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        {exp.location}
                      </span>
                    )}
                    {exp.company_website && (
                      <a
                        href={exp.company_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-brand-50 text-brand-600 px-2.5 py-1 rounded-full flex items-center gap-1 hover:bg-brand-100 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Website
                      </a>
                    )}
                    {exp.field_of_work && (
                      <span className="text-xs bg-brand-50 text-brand-600 px-2.5 py-1 rounded-full">{getFieldOfWorkLabel(exp.field_of_work)}</span>
                    )}
                  </div>

                  {exp.description && (
                    <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{exp.description}</p>
                  )}

                  {exp.reason_for_leaving && (
                    <div className="mt-2.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Reason for leaving</p>
                      <p className="text-sm text-gray-600">{exp.reason_for_leaving}</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">No work experience yet</h3>
              <p className="text-sm text-gray-500 mb-5 max-w-xs mx-auto">Start building your career timeline by adding your employment history.</p>
              <Link to="/experience/add">
                <Button>Add Your First Experience</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
